import { z } from 'zod'
import { aiModelsSchema } from './providers.js'

/** Inline helper — avoids cross-package import that causes bundler TDZ issues */
const encodeMetadata = (metadata: Record<string, unknown>) => JSON.stringify(metadata)

export const personalities = ['neutral', 'friendly', 'professional', 'witty', 'creative'] as const
const personalitySchema = z.enum(personalities)

export const aiContexts = ['global', 'song:section', 'song:line', 'song:image', 'song:music', 'advisor'] as const

type AIContextName = (typeof aiContexts)[number]

type AIContextDescription = {
  label: string
  description: string
}

export const aiContextDescriptions = {
  global: {
    label: 'Global',
    description: 'Global settings for all AI contexts',
  },
  'song:section': {
    label: 'Song Section',
    description: 'Settings for generating song sections',
  },
  'song:line': {
    label: 'Song Line',
    description: 'Settings for generating song lines',
  },
  'song:image': {
    label: 'Song Image',
    description: 'Settings for generating images from song lyrics',
  },
  'song:music': {
    label: 'Song Music',
    description: 'Settings for generating music clips from song lyrics',
  },
  advisor: {
    label: 'Musical Advisor',
    description: 'Settings for the musical advisor AI',
  },
} satisfies Record<AIContextName, AIContextDescription>

const aiContextSchema = z.enum(aiContexts).describe('The context for the AI')

/** Settings for a particular context */
export const aiContextSettingsSchema = z
  .object({
    model: aiModelsSchema.optional().catch(undefined),
    // General system prompt for the context
    systemPrompt: z
      .string()
      .describe(
        encodeMetadata({
          label: 'System Prompt',
          multiline: true,
        })
      )
      .optional(),

    // Style of the output, e.g., impressionist, modern, etc.
    defaultPrompt: z
      .string()
      .describe(
        encodeMetadata({
          label: 'Default prompt text for non-interactive AI',
          multiline: true,
        })
      )
      .optional(),

    // Personality of the AI
    personality: personalitySchema.optional(),

    // Style of the output, e.g., impressionist, modern, etc.
    textStyle: z
      .string()
      .describe(
        encodeMetadata({
          label: 'Style for generated text',
          multiline: true,
        })
      )
      .optional(),

    // Style of the output, e.g., impressionist, modern, etc.
    imageStyle: z
      .string()
      .describe(
        encodeMetadata({
          label: 'Style of generated images',
          multiline: true,
        })
      )
      .optional(),

    // Style of the music clip, e.g., jazz, classical, etc.
    musicStyle: z
      .string()
      .describe(
        encodeMetadata({
          label: 'Style of generated music',
          multiline: true,
        })
      )
      .optional(),

    //
  })
  .describe('AI settings for a context')

export type AIContextSettings = z.infer<typeof aiContextSettingsSchema>

export type AIContext = z.infer<typeof aiContextSchema>

// Basic schema for the AI Core Message
export const aiBasicModelMessageSchema = z.object({
  role: z.enum(['system', 'user', 'assistant', 'tool']),
  // The text content of the message
  content: z.string(),
})
export type AIBasicModelMessage = z.infer<typeof aiBasicModelMessageSchema>

// Full AI Configuration
export const aiConfigSchema = z.object({
  // Room for more configuration settings here
  contexts: z.record(aiContextSchema, aiContextSettingsSchema),
})

export type AIConfig = z.infer<typeof aiConfigSchema>
export function isAiConfig(obj: unknown): obj is AIConfig {
  return aiConfigSchema.safeParse(obj).success
}

// Field metadata for the AI config editor UI

export interface AIFieldMeta {
  label: string
  description: string
  placeholder?: string
}

type AIContextSettingsKey = keyof AIContextSettings

/** Per-context field visibility and metadata. Only fields listed here are shown in the editor for that context. */
export const aiContextFieldConfig: Record<AIContext, Partial<Record<AIContextSettingsKey, AIFieldMeta>>> = {
  global: {
    model: {
      label: 'Default AI Model',
      description: 'The AI model used when no context-specific model is set.',
    },
    systemPrompt: {
      label: 'System Instructions',
      description: 'Base instructions sent to the AI for all contexts. Context-specific instructions are added on top.',
      placeholder: 'e.g., You are a creative assistant for songwriting...',
    },
    personality: {
      label: 'Personality',
      description: 'The overall tone and character of AI responses.',
    },
    textStyle: {
      label: 'Text Style',
      description: 'Style instructions applied to all generated text (lyrics, advice, etc.).',
      placeholder: 'e.g., poetic, conversational, minimalist...',
    },
    imageStyle: {
      label: 'Image Style',
      description: 'Default visual style for AI-generated images (album art, illustrations).',
      placeholder: 'e.g., impressionist painting, minimalist vector art...',
    },
    musicStyle: {
      label: 'Music Style',
      description: 'Default style for AI-generated music clips.',
      placeholder: 'e.g., acoustic folk, electronic ambient...',
    },
  },
  'song:section': {
    model: { label: 'Model', description: 'AI model for generating song sections (verses, choruses, etc.).' },
    systemPrompt: {
      label: 'Custom Instructions',
      description: 'Additional instructions for section generation. Merged with global instructions.',
      placeholder: 'e.g., Write in the style of 1960s folk music...',
    },
    personality: { label: 'Personality', description: 'Tone for generated lyrics.' },
    textStyle: {
      label: 'Lyric Style',
      description: 'Style for the generated section text.',
      placeholder: 'e.g., metaphorical, storytelling, rhyming couplets...',
    },
  },
  'song:line': {
    model: { label: 'Model', description: 'AI model for generating individual song lines.' },
    systemPrompt: {
      label: 'Custom Instructions',
      description: 'Additional instructions for line generation.',
      placeholder: 'e.g., Keep lines under 10 words...',
    },
    personality: { label: 'Personality', description: 'Tone for generated lines.' },
    textStyle: {
      label: 'Line Style',
      description: 'Style for individual line generation.',
      placeholder: 'e.g., concise, evocative...',
    },
  },
  'song:image': {
    model: { label: 'Model', description: 'AI model for creating image prompts from lyrics.' },
    systemPrompt: {
      label: 'Custom Instructions',
      description: 'Instructions for image prompt generation.',
      placeholder: 'e.g., Focus on nature imagery and warm colors...',
    },
    imageStyle: {
      label: 'Image Style',
      description: 'Visual style for generated album art and illustrations.',
      placeholder: 'e.g., watercolor, photorealistic, retro poster...',
    },
  },
  'song:music': {
    model: { label: 'Model', description: 'AI model for creating music generation prompts.' },
    systemPrompt: {
      label: 'Custom Instructions',
      description: 'Instructions for music prompt generation.',
      placeholder: 'e.g., Emphasize acoustic instruments...',
    },
    musicStyle: {
      label: 'Music Style',
      description: 'Musical style for generated clips.',
      placeholder: 'e.g., lo-fi jazz, indie rock, orchestral...',
    },
  },
  advisor: {
    model: { label: 'Model', description: 'AI model for the musical advisor chat.' },
    systemPrompt: {
      label: 'Custom Instructions',
      description: 'Additional instructions for the advisor.',
      placeholder: 'e.g., Focus on chord progressions and song structure...',
    },
    personality: { label: 'Personality', description: "The advisor's conversational tone." },
    textStyle: {
      label: 'Response Style',
      description: 'How the advisor formats its responses.',
      placeholder: 'e.g., brief and actionable, detailed and educational...',
    },
  },
}
